import { getAppConfig } from './config/app-config';
import { CONSTANTS } from './config/constants';
import { getStr } from './util';

const { countryCode, joodExpiredFlag } = getAppConfig();
const { BASIC, VIP, SMART, NOPRIME, VIPPRIME, BASICPRIME, SMARTPRIME } = CONSTANTS;

export function getJoodDayCredits(priceType, algoliaProductHit) {
  const priceMap = {
    BASICPRIME:
      algoliaProductHit?.basicWalletCredits || algoliaProductHit?.productBasicPrimeData?.totalPrimeDiscount?.value,
    VIPPRIME: algoliaProductHit?.vipWalletCredits || algoliaProductHit?.productVipPrimeData?.totalPrimeDiscount?.value,
    SMARTPRIME: selectCorrectSmartPrice(
      algoliaProductHit?.smartFirstWalletCredits || algoliaProductHit?.productSmartPrimeData?.totalPrimeDiscount?.value,
      algoliaProductHit?.smartRepeatingWalletCredits ||
        algoliaProductHit?.productSmartPrimeData?.totalPrimeDiscount?.value
    ),
  };
  return priceMap[priceType] > 0 ? priceMap[priceType] : 0;
}
export function getSavedPercentVisibility(algoliaProductHit) {
  return countryCode !== 'OM' || algoliaProductHit?.priceValueDiscountPercentageFlag;
}

/**
 * This function returns either SmartFirst or SmartRepeating price based on the user stat.
 * This can be used to select all different prices (tactical, non-tactical etc) for smart users in one place.
 * @param smartFirstPrice Price to use if user is smart first.
 * @param smartRepeatingPrice Price to use if user is smart repeating.
 * @returns {number} Selected first / repeating price based on user.
 */
export function selectCorrectSmartPrice(smartFirstPrice, smartRepeatingPrice) {
  const { isUserSmartFirstEligible } = getAppConfig();
  if (isUserSmartFirstEligible && smartFirstPrice > 0) {
    return smartFirstPrice;
  }
  return smartRepeatingPrice > 0 ? smartRepeatingPrice : 0;
}

export function selectCorrectSmartPromoBoolean(smartFirstPromo, smartRepeatingPromo) {
  const { isUserSmartFirstEligible } = getAppConfig();
  if (isUserSmartFirstEligible) {
    return smartFirstPromo;
  }
  return smartRepeatingPromo;
}

export function getHasSavings(joodType, userJoodMembershipType, savedPrice) {
  const isPrimeMember = [BASICPRIME, VIPPRIME, SMARTPRIME].includes(userJoodMembershipType);
  const isExpired = [BASIC, VIP, SMART].includes(joodExpiredFlag);

  if (!isPrimeMember || isExpired) {
    return savedPrice > 0 && joodType === NOPRIME;
  }
  return savedPrice > 0;
}
export function getJoodDayBannerDetails(userJoodMembershipType) {
  const suffixMap = {
    BASICPRIME: BASIC,
    VIPPRIME: VIP,
    SMARTPRIME: SMART,
  };
  const suffix = suffixMap[userJoodMembershipType] || 'NONPRIME';
  const membershipType = userJoodMembershipType in suffixMap ? userJoodMembershipType : 'NOPRIME';

  const output = {
    textColor: getStr(`prime.jood.day.color.${suffix}`, [], '#FFFFFF'),
    backgroundImage: getStr(`prime.jood.day.bg.${suffix}`, [], ''),
    foregroundImage: getStr(`prime.jood.day.logo.${suffix}`, [], ''),
    borderColor: getStr(`extra.joodday.pricecard.border.${membershipType}`, [], '#00569A'),
    headingBorderColor: getStr(`extra.joodday.heading.border.${membershipType}`, [], '#00569A'),
    priceColor: getStr(`extra.joodday.price.color.${membershipType}`, [], '#FFFFFF'),
    timerTextColor: getStr(`extra.joodday.timer.text.color.${membershipType}`, [], '#FFFFFF'),
    moreCashpointTextColor: getStr(`extra.joodday.more.cashpoint.text.color.${membershipType}`, [], '#FFFFFF'),
    cashpointBackgroundImage: getStr(`extra.joodday.cashpoint.bg.${membershipType}`, [], ''),
    shortDescriptionColor: getStr(`extra.joodday.short.desc.color.${membershipType}`, [], '#FFFFFF'),
  };
  return output;
}

export function getServiceJoodCardType(userJoodMembershipType, expiredMembershipType, vasPrice, isPrimeNoPlan) {
  if (userJoodMembershipType === VIPPRIME) {
    if (vasPrice?.vipPrimeDiscountPrice?.value > 0) return 'VIPCARD_BG';
    if (vasPrice?.noPlanOption) return isPrimeNoPlan ? 'VIPCARD_BG_NOPLAN' : NOPRIME;
  }

  if (userJoodMembershipType === BASICPRIME) {
    if (vasPrice?.basicPrimeDiscountPrice?.value > 0) return 'BASICCARD_BG';
    if (vasPrice?.noPlanOption) return isPrimeNoPlan ? 'BASICCARD_BG_NOPLAN' : NOPRIME;
  }

  if (userJoodMembershipType === SMARTPRIME && expiredMembershipType !== SMARTPRIME) {
    if (vasPrice?.smartPrimeDiscountPrice?.value > 0) return 'SMARTCARD_BG';
    if (vasPrice?.noPlanOption) return isPrimeNoPlan ? 'SMARTCARD_BG_NOPLAN' : NOPRIME;
  }

  if (expiredMembershipType === BASICPRIME && vasPrice?.basicPrimeDiscountPrice?.value > 0) return BASICPRIME;
  if (expiredMembershipType === SMARTPRIME && vasPrice?.smartPrimeDiscountPrice?.value > 0) return SMARTPRIME;
  if (
    (expiredMembershipType === VIPPRIME || userJoodMembershipType === NOPRIME) &&
    vasPrice?.vipPrimeDiscountPrice?.value > 0
  )
    return VIPPRIME;

  return NOPRIME;
}

/**
 * This function determines if the no-plan price should be treated as prime
 * based on the presence of a jood price in other vasPrices and the user's jood
 * membership type.
 * @param userJoodMembershipType user jood membership
 * @param allVasPrices array of all the prices in the current service
 * @returns {boolean} `true` if the no-plan price should be treated as prime.
 */
export function getIsPrimeNoPlan(userJoodMembershipType, allVasPrices) {
  let output = false;
  for (const vasPrice of allVasPrices) {
    if (
      (userJoodMembershipType === VIPPRIME && vasPrice?.vipPrimeDiscountPrice?.value > 0) ||
      (userJoodMembershipType === BASICPRIME && vasPrice?.basicPrimeDiscountPrice?.value > 0) ||
      (userJoodMembershipType === SMARTPRIME && vasPrice?.smartPrimeDiscountPrice?.value > 0)
    ) {
      output = true;
      break;
    }
  }
  return output;
}
